<template>
    <div class="eco-elite-tab">
        <div v-if="ecoEliteLoading" class="text-center py-8">
            <v-progress-circular indeterminate size="32" color="teal-darken-2" />
            <div class="text-body-1 mt-3 text-grey-darken-1">{{ $t('loading') }}...</div>
        </div>

        <div v-else-if="ecoEliteData" class="eco-elite-content">
            <!-- Tier and Progress Section -->
            <v-card class="mb-4 elevation-2">
                <v-card-text class="pa-6">
                    <v-row align="center" class="mb-4">
                        <v-col cols="auto">
                            <v-avatar size="64" color="teal-darken-2" class="elevation-3">
                                <v-icon size="32" color="white">mdi-leaf</v-icon>
                            </v-avatar>
                        </v-col>
                        <v-col>
                            <div class="text-h5 font-weight-bold text-teal-darken-2">{{ ecoEliteTierName }}</div>
                            <div
                                v-if="pointsToUpgrade !== null"
                                class="text-subtitle-1 text-teal-darken-2 font-weight-medium"
                            >
                                {{ pointsToUpgrade }} {{ $t('points_to_upgrade_to_next_tier') }}
                            </div>
                            <div v-else class="text-subtitle-1 text-grey-darken-1">{{ $t('eco_elite_tier') }}</div>
                            <div class="text-body-2 text-grey-darken-1 mt-1">
                                {{ $t('member_since') }}: {{ memberSinceFormatted }}
                            </div>
                            <div class="text-body-2 text-grey-darken-1">{{ $t('membership_id') }}: {{ clientId }}</div>
                        </v-col>
                        <v-col cols="auto" class="text-right">
                            <div class="text-h4 font-weight-bold text-teal-darken-2">
                                {{ ecoEliteData.totalPoints || 0 }}
                            </div>
                            <div class="text-caption text-grey-darken-1">{{ $t('total_points') }}</div>
                        </v-col>
                    </v-row>

                    <v-progress-linear
                        :model-value="ecoEliteData.progress || 0"
                        height="12"
                        color="teal-darken-2"
                        bg-color="grey-lighten-3"
                        rounded
                        class="mb-3"
                    />
                    <div class="d-flex justify-space-between align-center">
                        <span class="text-body-2 font-weight-bold text-teal-darken-2"
                            >{{ $t('progress') }}: {{ ecoEliteData.progress || 0 }}%</span
                        >
                    </div>
                </v-card-text>
            </v-card>

            <!-- Statistics Grid -->
            <v-row>
                <v-col cols="12" md="6" lg="3">
                    <v-card class="text-center pa-4 elevation-2 h-100">
                        <v-icon size="32" color="teal-darken-2" class="mb-2">mdi-earth</v-icon>
                        <div class="text-h6 font-weight-bold text-teal-darken-2">
                            {{ ecoEliteData.totalKgCO2Saved || 0 }} kg
                        </div>
                        <div class="text-caption text-grey-darken-1">{{ $t('total_kg_co2_saved') }}</div>
                    </v-card>
                </v-col>

                <v-col cols="12" md="6" lg="3">
                    <v-card class="text-center pa-4 elevation-2 h-100">
                        <v-icon size="32" color="teal-darken-2" class="mb-2">mdi-currency-eur</v-icon>
                        <div class="text-h6 font-weight-bold text-teal-darken-2">
                            €{{ ecoEliteData.totalSavedAmount || 0 }}
                        </div>
                        <div class="text-caption text-grey-darken-1">{{ $t('total_saved_amount') }}</div>
                    </v-card>
                </v-col>

                <v-col cols="12" md="6" lg="3">
                    <v-card class="text-center pa-4 elevation-2 h-100">
                        <v-icon size="32" color="teal-darken-2" class="mb-2">mdi-calendar-check</v-icon>
                        <div class="text-h6 font-weight-bold text-teal-darken-2">
                            {{ client.reservationsNumber || 0 }}
                        </div>
                        <div class="text-caption text-grey-darken-1">{{ $t('total_bookings') }}</div>
                    </v-card>
                </v-col>

                <v-col v-if="totalSpent !== null" cols="12" md="6" lg="3">
                    <v-card class="text-center pa-4 elevation-2 h-100">
                        <v-icon size="32" color="teal-darken-2" class="mb-2">mdi-cash-multiple</v-icon>
                        <div class="text-h6 font-weight-bold text-teal-darken-2">€{{ totalSpent.toFixed(2) }}</div>
                        <div class="text-caption text-grey-darken-1">{{ $t('total_spent') }}</div>
                    </v-card>
                </v-col>
            </v-row>
        </div>

        <v-card v-else class="text-center pa-8 elevation-2">
            <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-leaf-off</v-icon>
            <div class="text-h6 text-grey-darken-1 mb-2">{{ $t('eco_elite_no_data') }}</div>
            <div class="text-body-2 text-grey">{{ $t('eco_elite_no_data_description') }}</div>
        </v-card>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator'

    @Component
    export class EcoElite extends Vue {
        @Prop({required: true}) clientId: number
        @Prop({required: true}) client: any

        ecoEliteData: any = null
        ecoEliteLoading: boolean = false
        loyaltyCategories: any[] = []
        totalSpent: number | null = null
        firstOrderDate: string | null = null

        @Watch('clientId', {immediate: true})
        async onClientIdChange(newClientId: number) {
            if (newClientId) {
                await this.loadLoyaltyCategories()
                await this.loadEcoEliteData()
                await this.loadTotalSpent()
            }
        }

        async loadLoyaltyCategories(): Promise<void> {
            try {
                this.loyaltyCategories = await this.$axios.$get('/zsh/api/loyalty/categories')
            } catch (e) {
                console.error('Failed to load loyalty categories:', e)
                this.loyaltyCategories = []
            }
        }

        async loadEcoEliteData(): Promise<void> {
            if (!this.clientId) return

            this.ecoEliteLoading = true
            try {
                // Use direct API call since this endpoint is not in the standard API modules
                this.ecoEliteData = await this.$axios.$get(`zsh/api/cms/customers/${this.clientId}/loyalty/status`)
            } catch (e) {
                console.error('Failed to load eco elite data:', e)
                this.ecoEliteData = null
            } finally {
                this.ecoEliteLoading = false
            }
        }

        async loadTotalSpent(): Promise<void> {
            if (!this.clientId) return

            try {
                // Reset first order date before loading
                this.firstOrderDate = null
                // Use orders API directly to get customer orders and calculate total
                await this.calculateTotalFromOrders()
            } catch (e) {
                console.error('Failed to load total spent:', e)
                this.totalSpent = null
                this.firstOrderDate = null
            }
        }

        async calculateTotalFromOrders(): Promise<void> {
            try {
                let allOrders: any[] = []
                let offset = 0
                const limit = 100 // Maximum allowed limit
                let hasMoreOrders = true

                // Paginate through all orders
                while (hasMoreOrders) {
                    const ordersData = await this.$axios.$get('/api/orders', {
                        params: {
                            clientId: this.clientId, // Try clientId parameter
                            responseType: 'SHORT',
                            limit: limit,
                            offset: offset,
                            orderType: 'TO', // Add required orderType parameter
                        },
                    })

                    if (ordersData.orders && ordersData.orders.length > 0) {
                        allOrders = allOrders.concat(ordersData.orders)
                        offset += limit

                        // Check if we got fewer orders than the limit (last page)
                        hasMoreOrders = ordersData.orders.length === limit
                    } else {
                        hasMoreOrders = false
                    }
                }

                if (allOrders.length > 0) {
                    // Find the earliest order date
                    const sortedOrders = allOrders.sort((a, b) => {
                        const dateA = new Date(a.creationDate || a.orderDate || a.date)
                        const dateB = new Date(b.creationDate || b.orderDate || b.date)
                        return dateA.getTime() - dateB.getTime()
                    })

                    if (
                        sortedOrders[0] &&
                        (sortedOrders[0].creationDate || sortedOrders[0].orderDate || sortedOrders[0].date)
                    ) {
                        this.firstOrderDate =
                            sortedOrders[0].creationDate || sortedOrders[0].orderDate || sortedOrders[0].date
                    }

                    // Sum up all order totals
                    this.totalSpent = allOrders.reduce((total: number, order: any) => {
                        let orderTotal = 0

                        // Find the CLIENT type in orderPrices array
                        if (order.orderPrices && Array.isArray(order.orderPrices)) {
                            const clientPrices = order.orderPrices.find(
                                (priceGroup: any) => priceGroup.type === 'CLIENT'
                            )

                            if (clientPrices && clientPrices.orderPrices && Array.isArray(clientPrices.orderPrices)) {
                                // Sum all paidAmount values from CLIENT orderPrices
                                orderTotal = clientPrices.orderPrices.reduce((sum: number, price: any) => {
                                    return sum + (price.paidAmount || 0)
                                }, 0)
                            }
                        }

                        return total + orderTotal
                    }, 0)

                    // Round to 2 decimal places (cents) using proper currency rounding
                    this.totalSpent = Math.round(this.totalSpent * 100) / 100
                } else {
                    this.totalSpent = 0
                }
            } catch (e) {
                console.error('Failed to calculate total from orders:', e)
                // Try alternative parameter names
                await this.tryAlternativeOrdersAPI()
            }
        }

        async tryAlternativeOrdersAPI(): Promise<void> {
            try {
                let allOrders: any[] = []
                let offset = 0
                const limit = 100 // Maximum allowed limit
                let hasMoreOrders = true

                // Paginate through all orders with alternative parameter
                while (hasMoreOrders) {
                    const ordersData = await this.$axios.$get('/api/orders', {
                        params: {
                            customerId: this.clientId, // Try customerId
                            responseType: 'SHORT',
                            limit: limit,
                            offset: offset,
                            orderType: 'TO',
                        },
                    })

                    if (ordersData.orders && ordersData.orders.length > 0) {
                        allOrders = allOrders.concat(ordersData.orders)
                        offset += limit

                        // Check if we got fewer orders than the limit (last page)
                        hasMoreOrders = ordersData.orders.length === limit
                    } else {
                        hasMoreOrders = false
                    }
                }

                if (allOrders.length > 0) {
                    // Sum up all order totals
                    this.totalSpent = allOrders.reduce((total: number, order: any) => {
                        let orderTotal = 0

                        // Find the CLIENT type in orderPrices array
                        if (order.orderPrices && Array.isArray(order.orderPrices)) {
                            const clientPrices = order.orderPrices.find(
                                (priceGroup: any) => priceGroup.type === 'CLIENT'
                            )

                            if (clientPrices && clientPrices.orderPrices && Array.isArray(clientPrices.orderPrices)) {
                                // Sum all paidAmount values from CLIENT orderPrices
                                orderTotal = clientPrices.orderPrices.reduce((sum: number, price: any) => {
                                    return sum + (price.paidAmount || 0)
                                }, 0)
                            }
                        }

                        return total + orderTotal
                    }, 0)

                    // Round to 2 decimal places (cents) using proper currency rounding
                    this.totalSpent = Math.round(this.totalSpent * 100) / 100
                } else {
                    this.totalSpent = 0
                    this.firstOrderDate = null
                }
            } catch (e) {
                console.error('Failed with alternative orders API:', e)
                this.totalSpent = null
                this.firstOrderDate = null
            }
        }

        get ecoEliteTierName(): string {
            if (!this.ecoEliteData || !this.loyaltyCategories.length) {
                return '0'
            }

            const tier = this.loyaltyCategories.find(category => category.id === this.ecoEliteData.ecoEliteTierId)
            return tier ? tier.name : this.ecoEliteData.ecoEliteTierId?.toString() || '0'
        }

        get pointsToUpgrade(): number | null {
            if (!this.ecoEliteData || !this.loyaltyCategories.length) {
                return null
            }

            const currentPoints = this.ecoEliteData.totalPoints || 0

            // Find the next tier with higher minPoints
            const nextTier = this.loyaltyCategories
                .filter(category => category.minPoints > currentPoints)
                .sort((a, b) => a.minPoints - b.minPoints)[0]

            if (nextTier) {
                return nextTier.minPoints - currentPoints
            }

            // If already at highest tier, return null
            return null
        }

        get memberSinceFormatted(): string {
            // Use registration date first, then fall back to first order date
            const dateToUse = this.client.registrationDate || this.firstOrderDate

            if (!dateToUse) {
                return ''
            }

            // Format as dd/mm/yyyy
            const date = new Date(dateToUse)
            const day = date.getDate().toString().padStart(2, '0')
            const month = (date.getMonth() + 1).toString().padStart(2, '0')
            const year = date.getFullYear()

            return `${day}/${month}/${year}`
        }
    }

    export default EcoElite
</script>

<style scoped>
    .eco-elite-tab {
        max-width: 100%;
    }

    .eco-elite-content {
        max-width: 100%;
    }

    .eco-elite-detail-row {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
    }

    .eco-elite-detail-row:last-child {
        margin-bottom: 0;
    }

    .v-card {
        transition: all 0.3s ease;
    }

    .v-card:hover {
        transform: translateY(-2px);
    }

    .v-progress-linear {
        border-radius: 4px;
    }
</style>
